import { useContext } from 'react'
import { DateTime } from 'luxon'

import { launchModal } from '@domain-group/javascript-utils'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import PageContext from '@contexts/pageContext'

import { baseURL } from '@functions/api'
import { mergeAndStringifyEntityObjects } from '@functions/entity'
import { getDeadlineStatuses, getNextDeadline } from '@functions/publicationEdition'
import { pendingStatusKeys } from '@functions/status'

import useArtworks from '@hooks/useArtworks'
import useCurrentUser from '@hooks/useCurrentUser'
import useDispatch from '@hooks/useDispatch'
import useOrderItems from '@hooks/useOrderItems'
import usePublicationEditions from '@hooks/usePublicationEditions'
import useSelector from '@hooks/useSelector'

import * as orderActions from '@redux/modules/order'
import * as webhookActions from '@redux/modules/webhook'

import type { AppDispatch } from '@redux/store'
import type { DeleteParams, EntityOptions, ModuleState } from '@redux/modules/types'
import type { EntitiesState } from '@redux/entities'

import defaultFormState, { requiredFields } from '@models/order'
import type {
  AgencyModel,
  AvailableLiftover,
  CampaignModel,
  OrderItemModel,
  OrderModel,
  OrderRequestOptions,
  ProviderModel,
  PublicationBookingModel,
  PublicationEditionModel,
  PublicationModel,
  StatusModel,
} from '@models/types'
import { getCampaignPermissions, getCampaignType } from '@functions/campaign'
import useReduxAction from './useReduxAction'

export type GenerateUrlsOptions = {
  campaignId?: number | undefined,
  prefixRoute?: string,
}

export const generateUrls = (order?: Partial<OrderModel>, options?: GenerateUrlsOptions) => {
  const payload = encodeURIComponent(JSON.stringify({ order: { id: order?.id } }))
  const { campaignId, prefixRoute = 'systemManager' } = options || {}

  const newOrderModalPayload = encodeURIComponent(
    JSON.stringify({ agencyId: order?.agency_id, campaignId: order?.campaign_id }),
  )

  return {
    // eslint-disable-next-line max-len
    addCampaignOrderUrl: `#/campaigns/${order?.campaign_id}/edit/orders/pending?showModal=NewOrderModal&showModalPayload=
    ${newOrderModalPayload}`,
    approvedCampaignOrdersUrl: `#/campaigns/${order?.campaign_id}/edit/orders/approved`,
    approvedOrdersCsv: `${baseURL}/orders/export_approved_csv.json`,
    editOrderUrl: `#/systemManager/orders?showModal=EditOrderModal&showModalPayload=${payload}`,
    editorialsUrl: '#/newsDesk/editorials',
    orderIndexUrl: `#/${prefixRoute}/orders`,
    ordersOverviewUrl: '#/agency/dashboard/agencies/all/overview/orders',
    pendingCampaignOrdersUrl: `#/campaigns/${campaignId || order?.campaign_id}/edit/orders/pending`,
    requirePaginationUrl: '#/newsDesk/requirePagination',
  }
}

type AddItemToOrderParams = {
  existingOrderId: number,
  dispatch: AppDispatch,
  itemParams: object,
  requestOptions?: EntityOptions,
}

const addItemToOrder = (params: AddItemToOrderParams) => {
  const {
    existingOrderId, dispatch, itemParams, requestOptions,
  } = params
  const { addItemToOrder: addItemFn } = orderActions

  return dispatch(addItemFn(existingOrderId, itemParams, requestOptions))
}

type ApproveOrderPayload = {
  dispatch: AppDispatch,
  order: OrderModel,
  orderParams: Partial<OrderModel>,
  requestOptions?: EntityOptions,
}

export const approveOrder = (params: ApproveOrderPayload) => {
  const {
    dispatch, order, orderParams, requestOptions,
  } = params
  const { approveOrder: approveFn } = orderActions

  const updatedParams = {
    id: order.id,
    ...orderParams,
  }

  return dispatch(approveFn(updatedParams, requestOptions))
}

type CreateOrderParams = {
  dispatch: AppDispatch,
  order: Partial<OrderModel>,
  requestOptions?: EntityOptions,
}

const createOrder = (params: CreateOrderParams) => {
  const { dispatch, order, requestOptions } = params
  const { createOrder: createFn } = orderActions

  return dispatch(createFn(order, requestOptions))
}

type CreateOrdersParams = {
  dispatch: AppDispatch,
  itemPayload: object,
  requestOptions?: EntityOptions,
}

const createOrders = (params: CreateOrdersParams) => {
  const { dispatch, itemPayload, requestOptions } = params
  const { createOrders: createFn } = orderActions

  return dispatch(createFn(itemPayload, requestOptions))
}

const calculateProcessingFees = (orderItems: OrderItemModel[]) => {
  const total = orderItems.reduce((acc, item) => {
    if (item.processing_fee){
      return acc + item.processing_fee
    }

    return acc
  }, 0)

  return total
}

type CheckAllItemsOrderableParams = {
  orderItems: OrderItemModel[],
  serviceProducts: EntitiesState['serviceProducts'],
}

const checkAllItemsOrderable = (params: CheckAllItemsOrderableParams) => {
  const { orderItems, serviceProducts } = params

  const unorderableItem = orderItems.find((orderItem) => {
    const { service_product_id } = orderItem
    // @ts-expect-error TS2538
    const serviceProduct = serviceProducts[service_product_id] || {}

    return serviceProduct.id && !serviceProduct.orderable
  })

  return !unorderableItem
}

type CheckOrderUnapprovableParams = {
  orderItems: OrderItemModel[],
  statuses: EntitiesState['statuses'],
}

const checkOrderUnapprovable = (params: CheckOrderUnapprovableParams) => {
  const { orderItems, statuses } = params

  const unapprovableItem = orderItems.find((orderItem) => {
    const { status_id } = orderItem

    const status = status_id && statuses[status_id] ? statuses[status_id] : {}

    // @ts-expect-error TS2339
    return status.key === 'pending'
  })

  return !unapprovableItem
}

type CreateLiftoverOrderParams = {
  dispatch: AppDispatch,
  liftover: AvailableLiftover,
  order: OrderModel,
  requestOptions?: EntityOptions,
}

const createLiftoverOrder = (params: CreateLiftoverOrderParams) => {
  const {
    dispatch, liftover, order, requestOptions,
  } = params
  const { createOrder: createFn } = orderActions

  const {
    provider_price_list_item_id, provider_price_list_product_unlock_id, publication_edition_id, publication_id,
  } = liftover

  const newOrder = {
    agency_id: order.agency_id,
    campaign_id: order.campaign_id,
    order_type: 'publication',
    provider_price_list_product_unlock_id,
    publication_id,
    publication_edition_id,
    items: JSON.stringify([
      {
        price_list_item_id: provider_price_list_item_id,
        options: JSON.stringify([]),
      },
    ]),
  }

  // @ts-expect-error TS2345
  return dispatch(createFn(newOrder, requestOptions))
}

type UnapproveOrderPayload = {
  dispatch: AppDispatch,
  order: OrderModel,
  orderParams: Partial<OrderModel>,
  requestOptions?: EntityOptions,
}

export const unapproveOrder = (params: UnapproveOrderPayload) => {
  const {
    dispatch, order, orderParams, requestOptions,
  } = params

  const { unapproveOrder: unapproveFn } = orderActions

  const updatedParams = mergeAndStringifyEntityObjects(order, orderParams)

  return dispatch(unapproveFn(updatedParams, requestOptions))
}

type ReprocessAPIPayload = {
  order: OrderModel,
  dispatch: AppDispatch,
}

export const reprocessAPI = (params: ReprocessAPIPayload) => {
  const { order, dispatch } = params
  const { reprocessAPI: reprocessAPIFn } = orderActions

  return dispatch(reprocessAPIFn(order))
}

type UpdateOrderParams = {
  dispatch: AppDispatch,
  order: OrderModel,
  orderParams: Partial<OrderModel>,
  requestOptions?: EntityOptions,
}

const updateOrder = (params: UpdateOrderParams) => {
  const {
    order, orderParams, dispatch, requestOptions,
  } = params
  const { updateOrder: updateFn } = orderActions

  const updatedParams = mergeAndStringifyEntityObjects(order, orderParams)

  return dispatch(updateFn(updatedParams, requestOptions))
}

type BulkReconcileParams = {
  entity: Partial<OrderModel>,
  entityIds: number[],
}

type BulkReconcileOrdersParams = {
  bulkReconcileParams: BulkReconcileParams,
  dispatch: AppDispatch,
  requestOptions?: EntityOptions,
}

const bulkReconcileOrders = (params: BulkReconcileOrdersParams) => {
  const { bulkReconcileParams, dispatch, requestOptions } = params

  const { entityIds, entity } = bulkReconcileParams

  const { bulkReconcileOrders: bulkReconcileFn } = orderActions

  const updatedParams = {
    order: entity,
    orderIds: entityIds,
  }

  return dispatch(bulkReconcileFn(updatedParams, requestOptions))
}

type BulkStatusUpdateParams = {
  dispatch: AppDispatch,
  order: OrderModel,
  orderParams: Partial<OrderModel>,
  requestOptions?: EntityOptions,
}

const bulkStatusUpdate = (params: BulkStatusUpdateParams) => {
  const {
    order, orderParams, dispatch, requestOptions,
  } = params
  const { bulkStatusUpdate: bulkStatusUpdateFn } = orderActions

  const updatedParams = {
    id: order.id,
    status_id: orderParams.status_id,
  }

  return dispatch(bulkStatusUpdateFn(updatedParams, requestOptions))
}

type CreateOrderWithOrderItemParams = {
  dispatch: AppDispatch,
  orderItem: OrderItemModel,
  requestOptions?: EntityOptions,
}

const createOrderWithOrderItem = (params: CreateOrderWithOrderItemParams) => {
  const { dispatch, orderItem, requestOptions } = params
  const { createOrderWithOrderItem: createFn } = orderActions

  return dispatch(createFn(orderItem, requestOptions)).then((response) => {
    const { success } = response

    if (success){
      const { data } = response
      const { pendingCampaignOrdersUrl } = generateUrls(data)

      return {
        data,
        redirectUrl: pendingCampaignOrdersUrl,
        success,
      }
    }

    return response
  })
}

type ReprocessWebhookParams = {
  entity_id: number,
  entity_type: string,
  event: string,
}

const reprocessWebhook = (reprocessWebhooksParams: ReprocessWebhookParams, dispatch: AppDispatch) => {
  const { reprocessWebhook: reprocessWebhookFn } = webhookActions

  return dispatch(reprocessWebhookFn(reprocessWebhooksParams))
}

type DeleteOrderParams = {
  dispatch: AppDispatch,
  order: DeleteParams<OrderModel>,
}

const deleteOrder = (params: DeleteOrderParams) => {
  const { dispatch, order } = params
  const { deleteOrder: deleteFn } = orderActions

  return dispatch(deleteFn(order))
}

export const useRelations = (order: Partial<OrderModel> = {}) => {
  const {
    agency_id,
    campaign_id,
    provider_id,
    publication_booking_id,
    publication_edition_id,
    publication_id,
    status_id,
  } = order

  const {
    agencies, campaigns, providers, publications, publicationBookings, publicationEditions, statuses,
  } = useSelector(reduxState => reduxState.entities)

  const agency: Partial<AgencyModel> = agency_id && agencies[agency_id] ? agencies[agency_id] : {}
  const campaign: Partial<CampaignModel> = campaign_id && campaigns[campaign_id] ? campaigns[campaign_id] : {}
  const provider: Partial<ProviderModel> = provider_id && providers[provider_id] ? providers[provider_id] : {}
  const publication: Partial<PublicationModel> = publication_id && publications[publication_id] ? publications[publication_id] : {}
  const publicationBooking: Partial<PublicationBookingModel> = publication_booking_id && publicationBookings[publication_booking_id]
    ? publicationBookings[publication_booking_id]
    : {}
  const publicationEdition: Partial<PublicationEditionModel> = publication_edition_id && publicationEditions[publication_edition_id]
    ? publicationEditions[publication_edition_id]
    : {}
  const status: Partial<StatusModel> = status_id && statuses[status_id] ? statuses[status_id] : {}

  return {
    agency,
    campaign,
    provider,
    publication,
    publicationBooking,
    publicationEdition,
    status,
  }
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useOrderForm(order: Partial<OrderModel> = {}, options: UseFormOptions & CustomFormOptions = {}) {
  const { customRequiredFields = [], validateOn } = options

  const orderForm = useForm(
    defaultFormState,
    { entity: order, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [order.id, order.cache_key],
  )

  return orderForm
}

type Options = {
  performHttpRequests?: boolean,
  requestOptions?: OrderRequestOptions,
}

function useOrder(initEntity: Partial<OrderModel> = {}, options: Options = {}) {
  const { performHttpRequests, requestOptions: entityRequestOptions } = options

  // @ts-expect-error TS2322
  const { entity: order }: { entity: OrderModel } = useLatestEntity(initEntity, 'orders')

  const dispatch = useDispatch()

  const {
    agency, campaign, provider, publication, publicationBooking, publicationEdition, status,
  } = useRelations(order)

  const { agency_id, id } = order

  useReduxAction(
    'orders',
    'loadOrder',
    {
      entityId: id,
      ...entityRequestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: ({ loading }: ModuleState) => !!performHttpRequests && !loading,
    },
  )

  const currentDateTime = DateTime.now()
  const todayDateString = currentDateTime.toFormat('yyyy-MM-dd')

  // Need to load publication editions to get the deadlines in the order's page
  usePublicationEditions({
    filters: {
      publication_id: publication?.id,
      min_publication_date: todayDateString,
    },
    performHttpRequests: !!publication?.id,
  })

  const {
    artworks, serviceProducts, statuses,
  } = useSelector(reduxState => reduxState.entities)

  const {
    approving, creating, deleting, loading, updating,
  } = useSelector(reduxState => reduxState.orders)

  const { callbacks } = useContext(PageContext)

  const { currentUser, permissions } = useCurrentUser()

  const campaignPermissions = getCampaignPermissions({ user: currentUser, campaignType: getCampaignType(campaign) })
  // Artworks
  const artworksPayload = useArtworks({
    filters: {
      order_id: order.id,
    },
  })

  const { filteredArtworks } = artworksPayload

  const approvedArtworksPayload = useArtworks({
    filters: {
      order_id: order.id,
      status_key: 'approved',
    },
  })

  const { filteredArtworks: approvedArtworks } = approvedArtworksPayload

  // Order Items
  const orderItemsPayload = useOrderItems({
    filters: {
      order_id: order.id,
    },
  })

  const { filteredOrderItems } = orderItemsPayload

  const firstOrderItem = filteredOrderItems[0] || {}
  const firstArtwork = firstOrderItem.artwork_id && artworks[firstOrderItem.artwork_id]
    ? artworks[firstOrderItem.artwork_id]
    : {}

  const isPublicationOrder = order.order_type === 'publication'

  // @ts-expect-error TS2345
  const deadlineStatuses = isPublicationOrder ? getDeadlineStatuses(publicationEdition) : {}
  // @ts-expect-error TS2322
  const nextDeadline = isPublicationOrder ? getNextDeadline({ publicationEdition }) : null

  const artworkApproved = !!approvedArtworks.length && approvedArtworks.length === filteredArtworks.length
  const providerLocked = provider.id && !provider.active

  // does the user have customer permissions set?
  const userCustomPermissions = permissions.enableCustomPermissions

  const providerPermitsPriceOverride = provider?.options?.order_total_override || false
  const userPermittedPriceOverride = campaignPermissions?.orders?.order_total_override !== false

  // can the price be overridden?
  // if the user has custom permissions, use those settings
  // otherwise fall back to the provider settings
  const totalEditable = userCustomPermissions ? userPermittedPriceOverride : providerPermitsPriceOverride

  // @ts-expect-error TS2339
  const isPending = pendingStatusKeys.includes(status.key)
  const isApproved = !isPending

  const isOrderable = checkAllItemsOrderable({ orderItems: filteredOrderItems, serviceProducts })
  const canApprove = campaignPermissions?.orders?.approve || false
  const canUnapprove = campaignPermissions?.orders?.unapprove || false

  const isApprovable = isPending && !providerLocked && canApprove
  // @ts-expect-error TS2339
  const isBeforeBookingDeadline = deadlineStatuses?.booking?.beforeDeadline || false
  const isPaginated = !!publicationBooking.id

  const isBookable = !isPaginated && isBeforeBookingDeadline && status.key === 'pending'
  const isPublicationManageable = campaignPermissions?.publications?.manage || false
  const isReleased = !permissions || (!isPublicationManageable && artworkApproved) || status.key === 'approved'
  const isUnapprovable = canUnapprove
    && checkOrderUnapprovable({
      orderItems: filteredOrderItems,
      statuses,
    })

  const isArtworkApprovable = !!filteredArtworks.length
  && !artworkApproved && (campaignPermissions?.artwork?.approve || false)

  const isDeletable = !isPaginated
    && !artworkApproved
    && status.key === 'pending'
    && (campaignPermissions?.orders?.delete || false)

  const hasOrderItems = !!filteredOrderItems.length

  return {
    agency,
    approvedArtworks,
    approving,
    artworkApproved,
    callbacks: {
      addItemToOrder: (existingOrderId: number, itemParams: object, entityOptions?: EntityOptions) => addItemToOrder({
        existingOrderId,
        dispatch,
        itemParams,
        requestOptions: entityOptions,
      }),
      addLiftover: (modalParams: { orderItemId: number }) => launchModal({
        callbacks,
        modalKey: 'SelectOrderItemLiftoverModal',
        payload: {
          callbacks: {
            createLiftoverOrder: (params:
              { liftover: AvailableLiftover, entityOptions?: object }) => createLiftoverOrder({
              dispatch,
              liftover: params.liftover,
              order,
              requestOptions: params.entityOptions,
            }),
          },
          orderItemId: modalParams.orderItemId,
        },
      }),
      addNewOrder: () => launchModal({
        callbacks,
        modalKey: 'NewOrderModal',
        payload: {
          agencyId: agency_id,
          campaignId: campaign.id,
        },
      }),
      approveOrder: (orderParams: Partial<OrderModel>, requestOptions?: EntityOptions) => approveOrder({
        order,
        orderParams,
        dispatch,
        requestOptions,
      }),
      bulkReconcileOrders: (
        bulkReconcileParams: BulkReconcileParams,
        entityOptions?: EntityOptions,
      ) => bulkReconcileOrders({
        bulkReconcileParams,
        dispatch,
        requestOptions: entityOptions,
      }),
      bulkStatusUpdate: (orderParams: Partial<OrderModel>, requestOptions?: EntityOptions) => bulkStatusUpdate({
        order,
        orderParams,
        dispatch,
        requestOptions,
      }),
      confirmOrder: () => launchModal({
        callbacks,
        modalKey: 'ApproveOrderModal',
        payload: {
          order,
        },
      }),
      createCreditNoteAndDeleteOrder: (deleteOrderParams: { deleteFn: () => void }) => launchModal({
        callbacks,
        modalKey: 'CreateCreditNoteAndDeleteOrderModal',
        payload: {
          entity: order,
          ...deleteOrderParams,
        },
      }),
      createOrderWithOrderItem: (
        orderItem: OrderItemModel,
        requestOptions?: EntityOptions,
      ) => createOrderWithOrderItem({
        dispatch,
        orderItem,
        requestOptions,
      }),
      createOrder: (orderParams: Partial<OrderModel>, entityOptions?: EntityOptions) => createOrder({
        order: { ...orderParams, agency_id },
        dispatch,
        requestOptions: entityOptions,
      }),
      createOrders: (itemPayload: object, entityOptions?: EntityOptions) => createOrders({
        itemPayload,
        dispatch,
        requestOptions: entityOptions,
      }),
      deleteOrder: () => deleteOrder({ dispatch, order }),
      editOrderModal: () => launchModal({
        callbacks,
        modalKey: 'EditOrderModal',
        payload: { order },
      }),
      editOrderTotal: () => launchModal({
        callbacks,
        modalKey: 'EditOrderTotalModal',
        payload: { order },
      }),
      reprocessAPI: () => reprocessAPI({ order, dispatch }),
      reprocessWebhook: (reprocessWebhookParams: ReprocessWebhookParams) => reprocessWebhook(
        reprocessWebhookParams,
        dispatch,
      ),
      toggleOrderPaginationApproval: (requestOptions?: EntityOptions) => updateOrder({
        dispatch,
        order,
        orderParams: {
          pagination_approved: !order.pagination_approved,
        },
        requestOptions,
      }),
      unapproveOrder: (orderParams: Partial<OrderModel>, requestOptions?: EntityOptions) => unapproveOrder({
        dispatch, order, orderParams, requestOptions,
      }),
      updateOrder: (orderParams: Partial<OrderModel>, requestOptions?: EntityOptions) => updateOrder({
        order,
        orderParams,
        dispatch,
        requestOptions,
      }),
      unapproveOrderConfirmation: () => launchModal({
        callbacks,
        modalKey: 'UnapproveOrderConfirmationModal',
        payload: { order },
      }),
    },
    campaign,
    canApprove,
    canUnapprove,
    creating,
    deadlineStatuses,
    deleting,
    filteredArtworks,
    filteredOrderItems,
    firstArtwork,
    firstOrderItem,
    hasOrderItems,
    isApprovable,
    isApproved,
    isArtworkApprovable,
    isBeforeBookingDeadline,
    isBookable,
    isDeletable,
    isOrderable,
    isPaginated,
    isPending,
    isPublicationOrder,
    isReleased,
    isUnapprovable,
    loading,
    nextDeadline,
    order,
    processingFees: calculateProcessingFees(filteredOrderItems),
    provider,
    providerLocked,
    publication,
    publicationEdition,
    totalEditable,
    updating,
    urls: generateUrls(order),
  }
}

export type UseOrderPayload = ReturnType<typeof useOrder>

export default useOrder
